/**
 * Vendor Bot Admin - Marketing Page
 * Broadcast messages, coupons, promotions
 */

import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams } from 'react-router-dom';
import Layout from '../../components/Layout';
import Navbar from '../../components/Navbar';
import Card from '../../components/Card';
import Button from '../../components/Button';
import Modal from '../../components/Modal';
import { useTelegram } from '../../hooks/useTelegram';

const Section = styled.div`
  margin-bottom: var(--spacing-lg);
`;

const SectionTitle = styled.h3`
  font-size: var(--font-lg);
  font-weight: var(--font-semibold);
  color: var(--text-primary);
  margin-bottom: var(--spacing-md);
`;

const ActionCard = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
  margin-bottom: var(--spacing-md);
`;

const ActionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ActionIcon = styled.div`
  font-size: 32px;
`;

const ActionInfo = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
`;

const ActionTitle = styled.div`
  font-size: var(--font-md);
  font-weight: var(--font-semibold);
  color: var(--text-primary);
`;

const ActionDescription = styled.div`
  font-size: var(--font-sm);
  color: var(--text-secondary);
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
  margin-bottom: var(--spacing-md);
`;

const Label = styled.label`
  font-size: var(--font-sm);
  font-weight: var(--font-medium);
  color: var(--text-primary);
`;

const Textarea = styled.textarea`
  padding: var(--spacing-md);
  background: var(--bg-secondary);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  color: var(--text-primary);
  font-size: var(--font-md);
  min-height: 120px;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: var(--accent-green);
  }
`;

const Marketing: React.FC = () => {
  const { botUsername } = useParams<{ botUsername: string }>();
  const navigate = useNavigate();
  const { showBackButton, notificationFeedback } = useTelegram();
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [broadcastMessage, setBroadcastMessage] = useState('');

  useEffect(() => {
    showBackButton(() => navigate(`/vendor/${botUsername}/admin/dashboard`));
  }, [showBackButton, navigate, botUsername]);

  const handleSendBroadcast = () => {
    notificationFeedback('success');
    setShowBroadcastModal(false);
    setBroadcastMessage('');
  };

  return (
    <Layout header={<Navbar title="Marketing" showBack />}>
      <Section>
        <SectionTitle>Campaigns</SectionTitle>
        <ActionCard>
          <ActionHeader>
            <ActionIcon>📢</ActionIcon>
            <ActionInfo>
              <ActionTitle>Broadcast Message</ActionTitle>
              <ActionDescription>Send message to all customers</ActionDescription>
            </ActionInfo>
          </ActionHeader>
          <Button onClick={() => setShowBroadcastModal(true)}>Create Broadcast</Button>
        </ActionCard>

        <ActionCard>
          <ActionHeader>
            <ActionIcon>🎟️</ActionIcon>
            <ActionInfo>
              <ActionTitle>Coupons</ActionTitle>
              <ActionDescription>Create discount coupons</ActionDescription>
            </ActionInfo>
          </ActionHeader>
          <Button variant="secondary">Manage Coupons</Button>
        </ActionCard>

        <ActionCard>
          <ActionHeader>
            <ActionIcon>⚡</ActionIcon>
            <ActionInfo>
              <ActionTitle>Flash Sale</ActionTitle>
              <ActionDescription>Time-limited discounts</ActionDescription>
            </ActionInfo>
          </ActionHeader>
          <Button variant="secondary">Create Flash Sale</Button>
        </ActionCard>
      </Section>

      <Modal
        show={showBroadcastModal}
        onClose={() => setShowBroadcastModal(false)}
        title="Send Broadcast"
        footer={
          <>
            <Button variant="secondary" onClick={() => setShowBroadcastModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendBroadcast} disabled={!broadcastMessage.trim()}>
              Send
            </Button>
          </>
        }
      >
        <FormGroup>
          <Label>Message</Label>
          <Textarea
            value={broadcastMessage}
            onChange={(e) => setBroadcastMessage(e.target.value)}
            placeholder="Enter your broadcast message..."
          />
        </FormGroup>
        <p style={{ fontSize: 'var(--font-sm)', color: 'var(--text-secondary)' }}>
          This message will be sent to all {127} customers.
        </p>
      </Modal>
    </Layout>
  );
};

export default Marketing;
